import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Filter,
  Grid3X3,
  CalendarDays,
  RefreshCw,
  GraduationCap,
  CheckCircle,
  AlertCircle,
  X,
  ChevronDown,
  Info,
  Download,
} from 'lucide-react';
import { AcademicYearCalendarView } from '../components/annual-planning/AcademicYearCalendarView';
import { AcademicYearMonthlyView } from '../components/annual-planning/AcademicYearMonthlyView';
import { EventFormNew } from '../components/annual-planning/EventFormNew';
import {
  annualPlanningService,
  AcademicEvent,
  EventCategory,
  EventVisibility,
} from '../services/annualPlanningService';
import { holidaysService, PublicHoliday } from '../services/holidaysService';
import { calendarSettingsService, AcademicYearTemplate } from '../services/calendarSettingsService';
import examScheduleService, { ExamPeriods } from '../services/examScheduleService';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import { apiGet } from '@/utils/api';

type ViewMode = 'yearly' | 'monthly';

interface FilterState {
  categoryIds?: string[];
  visibility?: EventVisibility;
}

interface AcademicYear {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
}

// Toast component
interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-emerald-600' : type === 'error' ? 'bg-red-600' : 'bg-blue-600';
  const Icon = type === 'success' ? CheckCircle : type === 'error' ? AlertCircle : Info;

  return (
    <div className={`fixed bottom-4 right-4 z-50 ${bgColor} text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 max-w-md animate-in slide-in-from-bottom-5`}>
      <Icon size={20} />
      <span className="flex-1 text-sm">{message}</span>
      <button onClick={onClose} className="p-1 hover:bg-white/20 rounded transition-colors">
        <X size={16} />
      </button>
    </div>
  );
};

export const PlanningAnnuelPage: React.FC = () => {
  const { hasPermission } = usePermissions();

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('yearly');
  const [, setTemplate] = useState<AcademicYearTemplate | null>(null);

  // Academic year state - Calculate dynamically based on template
  // For academic year starting in September: if we're in Sept-Dec, use current year; if Jan-Aug, use previous year
  const [academicYearStart, setAcademicYearStart] = useState(() => {
    const now = new Date();
    const currentMonth = now.getMonth(); // 0-indexed: 0=Jan, 8=Sept
    // Default: September (month 8) is start of academic year
    // If current month >= September (8), we're in the first half of academic year (e.g., Sept 2025 = 2025-26)
    // If current month < September (0-7), we're in the second half (e.g., Feb 2026 = 2025-26)
    return currentMonth >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  });

  // Data state
  const [events, setEvents] = useState<AcademicEvent[]>([]);
  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [holidays, setHolidays] = useState<PublicHoliday[]>([]);
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [currentAcademicYear, setCurrentAcademicYear] = useState<AcademicYear | null>(null);
  const [examPeriods, setExamPeriods] = useState<ExamPeriods | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // UI state
  const [showEventForm, setShowEventForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showYearSelector, setShowYearSelector] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<AcademicEvent | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [filters, setFilters] = useState<FilterState>({});

  // Use consolidated CALENDAR_ANNUAL_MANAGE permission for all event management
  const canManage = hasPermission(Permission.CALENDAR_ANNUAL_MANAGE);

  // Load template (academic year start is calculated from initial state based on today's date)
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        const data = await calendarSettingsService.getAcademicYearTemplate();
        setTemplate(data);
        // Note: We don't recalculate academicYearStart here - it's correctly set in useState initializer
        // The template is only used to determine the start month for display purposes
      } catch (err) {
        console.error('Failed to load template:', err);
      }
    };
    loadTemplate();
  }, []);

  // Load categories
  const loadCategories = useCallback(async () => {
    try {
      const data = await annualPlanningService.getCategories();

      if (data && data.length > 0) {
        setCategories(data);
      } else if (canManage) {
        // Only try to initialize system categories if user has input permissions
        try {
          const initialized = await annualPlanningService.initializeSystemCategories();
          if (initialized && initialized.length > 0) {
            setCategories(initialized);
          }
        } catch (initErr) {
          console.error('Failed to initialize system categories:', initErr);
        }
      }
    } catch (err) {
      console.error('Failed to load categories:', err);
      // Only try to initialize if user has permissions
      if (canManage) {
        try {
          const initialized = await annualPlanningService.initializeSystemCategories();
          if (initialized && initialized.length > 0) {
            setCategories(initialized);
          }
        } catch (initErr) {
          console.error('Failed to initialize system categories:', initErr);
        }
      }
    }
  }, [canManage]);

  // Load academic years
  const loadAcademicYears = useCallback(async () => {
    try {
      const years = await apiGet<AcademicYear[]>('/academic-planning/years');
      if (years && years.length > 0) {
        setAcademicYears(years);

        // Find the academic year that matches the current display year (academicYearStart)
        // Priority: 1) Match by start date year, 2) Match by name pattern, 3) isCurrent flag, 4) first one
        let matchingYear = years.find((y: AcademicYear) => {
          const startDate = new Date(y.startDate);
          return startDate.getFullYear() === academicYearStart;
        });

        // If no match by date, try matching by name pattern (e.g., "2025-2026" or "2025-26")
        if (!matchingYear) {
          matchingYear = years.find((y: AcademicYear) => {
            const nameMatch = y.name.match(/^(\d{4})/);
            return nameMatch && parseInt(nameMatch[1]) === academicYearStart;
          });
        }

        // Only use fallback if we explicitly found no match for the calculated year
        // Don't change academicYearStart - it should stay as the correctly calculated value
        if (matchingYear) {
          setCurrentAcademicYear(matchingYear);
        } else {
          // No matching year in database - use first available but keep the display year unchanged
          const fallback = years.find((y: AcademicYear) => y.isCurrent) || years[0];
          if (fallback) {
            setCurrentAcademicYear(fallback);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load academic years:', err);
    }
  }, [academicYearStart]);

  // Load events - load all events and filter client-side for better UX
  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filter: {
        academicYearId?: string;
        visibility?: EventVisibility;
      } = {};

      if (currentAcademicYear) {
        filter.academicYearId = currentAcademicYear.id;
      }

      if (filters.visibility) {
        filter.visibility = filters.visibility;
      }

      const data = await annualPlanningService.getEvents(filter);
      setEvents(data);
    } catch (err) {
      console.error('Failed to load events:', err);
      setError('Erreur lors du chargement des événements');
    } finally {
      setLoading(false);
    }
  }, [filters.visibility, currentAcademicYear]);

  // Load holidays for the academic year
  const loadHolidays = useCallback(async () => {
    try {
      const holidaysYear1 = await holidaysService.getHolidays(academicYearStart);
      const holidaysYear2 = await holidaysService.getHolidays(academicYearStart + 1);
      setHolidays([...holidaysYear1, ...holidaysYear2]);
    } catch (err) {
      console.error('Failed to load holidays:', err);
    }
  }, [academicYearStart]);

  // Load exam periods for the academic year
  const loadExamPeriods = useCallback(async () => {
    if (!currentAcademicYear) return;
    try {
      const periods = await examScheduleService.getExamPeriods(currentAcademicYear.id);
      setExamPeriods(periods);
    } catch (err) {
      console.error('Failed to load exam periods:', err);
    }
  }, [currentAcademicYear]);

  // Convert exam periods to academic events for display in calendar
  const examPeriodEvents = useMemo((): AcademicEvent[] => {
    if (!examPeriods || !currentAcademicYear) return [];

    const periodEvents: AcademicEvent[] = [];
    const tenantId = localStorage.getItem('nexedu_tenant_id') || '';

    // Find the ACADEMIC category for exam periods
    const academicCategory = categories.find(c => c.code === 'ACADEMIC');

    // Helper to create an event
    const createPeriodEvent = (
      id: string,
      title: string,
      startDate: string | null,
      endDate: string | null,
      color: string
    ): AcademicEvent | null => {
      if (!startDate || !endDate) return null;
      return {
        id,
        tenantId,
        academicYearId: currentAcademicYear.id,
        categoryId: academicCategory?.id,
        title,
        description: `Période définie dans les paramètres des examens`,
        eventType: 'EXAM_PERIOD',
        startDate,
        endDate,
        isAllDay: true,
        isRecurring: false,
        color,
        visibility: 'ALL',
        isAcademic: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        category: academicCategory,
      };
    };

    // Add exam period 1
    const period1Event = createPeriodEvent(
      'exam-period-1',
      examPeriods.examPeriodType === 'SINGLE' ? 'Période d\'examens' : 'Examens Semestre 1',
      examPeriods.period1?.start,
      examPeriods.period1?.end,
      '#F97316' // Orange
    );
    if (period1Event) periodEvents.push(period1Event);

    // Add exam period 2 (only for PER_SEMESTER)
    if (examPeriods.examPeriodType === 'PER_SEMESTER' && examPeriods.period2) {
      const period2Event = createPeriodEvent(
        'exam-period-2',
        'Examens Semestre 2',
        examPeriods.period2.start,
        examPeriods.period2.end,
        '#F97316' // Orange
      );
      if (period2Event) periodEvents.push(period2Event);
    }

    // Add rattrapage periods (only if rattrapage is enabled)
    if (examPeriods.rattrapageEnabled) {
      // Rattrapage period 1
      const rattrapage1Event = createPeriodEvent(
        'exam-rattrapage-1',
        examPeriods.rattrapagePeriodType === 'SINGLE' ? 'Période de rattrapage' : 'Rattrapage Semestre 1',
        examPeriods.rattrapage1?.start,
        examPeriods.rattrapage1?.end,
        '#EAB308' // Yellow/Amber
      );
      if (rattrapage1Event) periodEvents.push(rattrapage1Event);

      // Rattrapage period 2 (only for PER_SEMESTER)
      if (examPeriods.rattrapagePeriodType === 'PER_SEMESTER' && examPeriods.rattrapage2) {
        const rattrapage2Event = createPeriodEvent(
          'exam-rattrapage-2',
          'Rattrapage Semestre 2',
          examPeriods.rattrapage2.start,
          examPeriods.rattrapage2.end,
          '#EAB308' // Yellow/Amber
        );
        if (rattrapage2Event) periodEvents.push(rattrapage2Event);
      }
    }

    return periodEvents;
  }, [examPeriods, currentAcademicYear, categories]);

  // Initial load
  useEffect(() => {
    loadCategories();
    loadAcademicYears();
  }, [loadCategories, loadAcademicYears]);

  useEffect(() => {
    if (currentAcademicYear) {
      loadEvents();
      loadExamPeriods();
    }
  }, [loadEvents, loadExamPeriods, currentAcademicYear]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowYearSelector(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Event handlers
  const handleEventClick = (event: AcademicEvent) => {
    // Synthetic exam/rattrapage period events are not editable records —
    // they mirror the exam settings, so show info instead of the form
    if (event.id.startsWith('exam-period-') || event.id.startsWith('exam-rattrapage-')) {
      setToast({
        message: `${event.title} : ${new Date(event.startDate).toLocaleDateString('fr-FR')} au ${new Date(event.endDate).toLocaleDateString('fr-FR')} (modifiable dans Paramètres → Examens)`,
        type: 'info',
      });
      return;
    }
    setSelectedEvent(event);
    setSelectedDate(undefined);
    setShowEventForm(true);
  };

  const handleDateClick = (date: Date) => {
    if (!canManage) return;
    setSelectedEvent(null);
    setSelectedDate(date);
    setShowEventForm(true);
  };

  const handleHolidayClick = (holiday: PublicHoliday) => {
    setToast({
      message: `${holiday.name} - ${new Date(holiday.startDate).toLocaleDateString('fr-FR')} au ${new Date(holiday.endDate).toLocaleDateString('fr-FR')}`,
      type: 'info'
    });
  };

  const handleEventSaved = () => {
    setShowEventForm(false);
    setSelectedEvent(null);
    setSelectedDate(undefined);
    loadEvents();
  };

  const handleEventDeleted = () => {
    setShowEventForm(false);
    setSelectedEvent(null);
    setSelectedDate(undefined);
    loadEvents();
  };

  const handleYearChange = async (newStartYear: number) => {
    setAcademicYearStart(newStartYear);

    // Find and set the matching academic year from database
    // This ensures events are saved to the correct academic year
    const matchingYear = academicYears.find((y: AcademicYear) => {
      // Try matching by start date year
      const startDate = new Date(y.startDate);
      if (startDate.getFullYear() === newStartYear) return true;

      // Try matching by name pattern (e.g., "2025-2026" or "2025-26")
      const nameMatch = y.name.match(/^(\d{4})/);
      return nameMatch && parseInt(nameMatch[1]) === newStartYear;
    });

    if (matchingYear) {
      setCurrentAcademicYear(matchingYear);
    }

    // Reload holidays for the new academic year
    try {
      const holidaysYear1 = await holidaysService.getHolidays(newStartYear);
      const holidaysYear2 = await holidaysService.getHolidays(newStartYear + 1);
      setHolidays([...holidaysYear1, ...holidaysYear2]);
    } catch (err) {
      console.error('Failed to load holidays for new year:', err);
    }
  };

  const handleAcademicYearSelect = (year: AcademicYear) => {
    setCurrentAcademicYear(year);
    setShowYearSelector(false);
    // Update the visual year based on the selected academic year
    const startDate = new Date(year.startDate);
    setAcademicYearStart(startDate.getFullYear());
  };

  const toggleCategoryFilter = (categoryId: string) => {
    setFilters(prev => {
      const currentIds = prev.categoryIds || [];
      const newIds = currentIds.includes(categoryId)
        ? currentIds.filter(id => id !== categoryId)
        : [...currentIds, categoryId];

      return {
        ...prev,
        categoryIds: newIds.length > 0 ? newIds : undefined,
      };
    });
  };

  const clearFilters = () => {
    setFilters({});
  };

  // Client-side filtered events (including exam period events)
  const filteredEvents = useMemo(() => {
    // Combine regular events with exam period events
    const allEvents = [...events, ...examPeriodEvents];

    if (!filters.categoryIds?.length) {
      return allEvents;
    }

    // Get selected category codes for comparison
    const selectedCategoryCodes = filters.categoryIds
      .map(id => categories.find(c => c.id === id)?.code)
      .filter(Boolean);

    return allEvents.filter(event => {
      // Include event if its category is in the selected filter
      const eventCategoryId = event.categoryId || event.category?.id || '';
      const eventCategoryCode = event.category?.code || categories.find(c => c.id === eventCategoryId)?.code;

      // Match by ID or by code
      return filters.categoryIds!.includes(eventCategoryId) ||
             (eventCategoryCode && selectedCategoryCodes.includes(eventCategoryCode));
    });
  }, [events, examPeriodEvents, filters.categoryIds, categories]);

  // Filter holidays based on selected category - holidays only show when:
  // 1. No filter is active (show all)
  // 2. "Période de Vacances" (VACATION) category is selected
  const filteredHolidays = useMemo(() => {
    if (!filters.categoryIds?.length) {
      // No filter active - show all holidays
      return holidays;
    }

    // Find the VACATION category
    const vacationCategory = categories.find(c => c.code === 'VACATION');

    // If VACATION category is selected, show holidays
    if (vacationCategory && filters.categoryIds.includes(vacationCategory.id)) {
      return holidays;
    }

    // Otherwise, hide holidays when other filters are active
    return [];
  }, [holidays, filters.categoryIds, categories]);

  // Export calendar to PDF
  const handleExportPDF = async () => {
    try {
      setToast({ message: 'Génération du PDF en cours...', type: 'info' });

      // Sort events chronologically
      const sortedEvents = [...filteredEvents].sort((a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
      );

      // Sort holidays chronologically
      const sortedHolidays = [...filteredHolidays].sort((a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
      );

      // Create HTML content for PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Planning Annuel ${academicYearStart}-${academicYearStart + 1}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 20px; color: #333; }
            h1 { font-size: 24px; margin-bottom: 5px; color: #1a1a1a; }
            h2 { font-size: 18px; margin: 20px 0 10px; color: #444; border-bottom: 2px solid #eee; padding-bottom: 5px; }
            .subtitle { font-size: 14px; color: #666; margin-bottom: 20px; }
            .legend { display: flex; flex-wrap: wrap; gap: 15px; margin: 15px 0 25px; padding: 10px; background: #f9f9f9; border-radius: 8px; }
            .legend-item { display: flex; align-items: center; gap: 6px; font-size: 12px; }
            .legend-color { width: 14px; height: 14px; border-radius: 3px; }
            .events-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            .events-table th, .events-table td { border: 1px solid #e0e0e0; padding: 8px 12px; text-align: left; font-size: 12px; }
            .events-table th { background: #f5f5f5; font-weight: 600; }
            .events-table tr:nth-child(even) { background: #fafafa; }
            .category-badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500; }
            .date { white-space: nowrap; }
            .footer { margin-top: 30px; font-size: 10px; color: #888; text-align: center; border-top: 1px solid #eee; padding-top: 15px; }
            @media print {
              body { padding: 10px; }
              .legend { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>Planning Annuel ${academicYearStart}-${academicYearStart + 1}</h1>
          <p class="subtitle">Généré le ${new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

          <div class="legend">
            <div class="legend-item">
              <div class="legend-color" style="background: #F59E0B;"></div>
              <span>Jours Fériés</span>
            </div>
            ${categories.filter(c => c.isActive).map(cat => `
              <div class="legend-item">
                <div class="legend-color" style="background: ${cat.color};"></div>
                <span>${cat.name}</span>
              </div>
            `).join('')}
          </div>

          <h2>Jours Fériés (${sortedHolidays.length})</h2>
          <table class="events-table">
            <thead>
              <tr>
                <th style="width: 25%;">Date</th>
                <th>Nom</th>
              </tr>
            </thead>
            <tbody>
              ${sortedHolidays.map(h => {
                const start = new Date(h.startDate);
                const end = new Date(h.endDate);
                const isSameDay = start.toDateString() === end.toDateString();
                return `
                  <tr>
                    <td class="date">${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}${!isSameDay ? ` - ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}</td>
                    <td>${h.name}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>

          <h2>Événements Académiques (${sortedEvents.length})</h2>
          <table class="events-table">
            <thead>
              <tr>
                <th style="width: 25%;">Date</th>
                <th style="width: 35%;">Événement</th>
                <th style="width: 20%;">Catégorie</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              ${sortedEvents.map(e => {
                const start = new Date(e.startDate);
                const end = new Date(e.endDate);
                const isSameDay = start.toDateString() === end.toDateString();
                const cat = e.category || categories.find(c => c.id === e.categoryId);
                return `
                  <tr>
                    <td class="date">${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}${!isSameDay ? ` - ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}</td>
                    <td><strong>${e.title}</strong></td>
                    <td><span class="category-badge" style="background: ${cat?.color || '#6B7280'}20; color: ${cat?.color || '#6B7280'};">${cat?.name || 'Autre'}</span></td>
                    <td>${e.description || '-'}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>

          <div class="footer">
            <p>NEXEDU - Planning Annuel ${academicYearStart}-${academicYearStart + 1}</p>
          </div>
        </body>
        </html>
      `;

      // Create a Blob and open in new window for printing
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');

      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            URL.revokeObjectURL(url);
          }, 250);
        };
        setToast({ message: 'PDF prêt à imprimer', type: 'success' });
      } else {
        setToast({ message: 'Veuillez autoriser les popups pour exporter', type: 'error' });
      }
    } catch (err) {
      console.error('Failed to export PDF:', err);
      setToast({ message: 'Erreur lors de l\'exportation', type: 'error' });
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg">
            <GraduationCap className="text-white" size={26} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Planning Annuel
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-gray-500 text-sm">
                Année Académique {academicYearStart}-{(academicYearStart + 1).toString().slice(-2)}
              </span>
              {academicYears.length > 0 && (
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => setShowYearSelector(!showYearSelector)}
                    className="flex items-center gap-1 text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full hover:bg-primary-200 transition-colors"
                  >
                    {academicYearStart}-{academicYearStart + 1}
                    <ChevronDown size={12} />
                  </button>
                  {showYearSelector && (
                    <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20 min-w-[150px]">
                      {academicYears.map(year => {
                        const yearStartMatch = year.name.match(/^(\d{4})/);
                        const yearStart = yearStartMatch ? parseInt(yearStartMatch[1]) : null;
                        return (
                          <button
                            key={year.id}
                            onClick={() => handleAcademicYearSelect(year)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                              yearStart === academicYearStart ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                            }`}
                          >
                            {year.name}
                            {year.isCurrent && (
                              <span className="ml-2 text-xs text-primary-500">(actuelle)</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('yearly')}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all
                ${viewMode === 'yearly'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                }
              `}
            >
              <Grid3X3 size={16} />
              Vue Annuelle
            </button>
            <button
              onClick={() => setViewMode('monthly')}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all
                ${viewMode === 'monthly'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                }
              `}
            >
              <CalendarDays size={16} />
              Vue Mensuelle
            </button>
          </div>

          {/* Filters Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors
              ${showFilters
                ? 'bg-primary-50 border-primary-200 text-primary-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }
            `}
          >
            <Filter size={18} />
            Filtres
            {(filters.categoryIds?.length || filters.visibility) && (
              <span className="w-2 h-2 bg-primary-500 rounded-full" />
            )}
          </button>

        </div>
      </div>


      {/* Filters Panel */}
      {showFilters && (
        <div className="mb-6 bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Filtres</h3>
            <button
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Réinitialiser
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Catégories
            </label>
            <div className="flex flex-wrap gap-2">
              {categories.filter(c => c.isActive).map(category => {
                const isSelected = filters.categoryIds?.includes(category.id);
                return (
                  <button
                    key={category.id}
                    onClick={() => toggleCategoryFilter(category.id)}
                    className={`
                      flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                      ${isSelected
                        ? 'bg-primary-100 text-primary-700 border-2 border-primary-300'
                        : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                      }
                    `}
                  >
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: category.color }}
                    />
                    {category.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-3">
            <AlertCircle size={20} className="text-red-600" />
            <span className="text-red-700">{error}</span>
            <button
              onClick={loadEvents}
              className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800"
            >
              <RefreshCw size={14} />
              Réessayer
            </button>
          </div>
        </div>
      )}

      {/* Calendar Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Chargement du calendrier...</p>
            </div>
          </div>
        ) : viewMode === 'yearly' ? (
          <AcademicYearCalendarView
            academicYearStart={academicYearStart}
            events={filteredEvents}
            categories={categories}
            holidays={filteredHolidays}
            selectedCategoryIds={filters.categoryIds}
            onYearChange={handleYearChange}
            onEventClick={handleEventClick}
            onDateClick={(canManage) ? handleDateClick : undefined}
            onDateRangeSelect={(canManage) ? (startDate: Date, endDate: Date) => {
              setSelectedEvent(null);
              setSelectedDate(startDate);
              setShowEventForm(true);
              // Store end date for multi-day event creation
              (window as unknown as { __calendarEndDate?: Date }).__calendarEndDate = endDate;
            } : undefined}
            onHolidayClick={handleHolidayClick}
          />
        ) : (
          <AcademicYearMonthlyView
            academicYearStart={academicYearStart}
            events={filteredEvents}
            categories={categories}
            holidays={filteredHolidays}
            selectedCategoryIds={filters.categoryIds}
            onEventClick={handleEventClick}
            onDateClick={(canManage) ? handleDateClick : undefined}
            onDateRangeSelect={(canManage) ? (startDate: Date, endDate: Date) => {
              setSelectedEvent(null);
              setSelectedDate(startDate);
              setShowEventForm(true);
              // Store end date for multi-day event creation
              (window as unknown as { __calendarEndDate?: Date }).__calendarEndDate = endDate;
            } : undefined}
            onHolidayClick={handleHolidayClick}
          />
        )}

        {/* Export Button */}
        {!loading && viewMode === 'yearly' && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              <Download size={16} />
              Exporter en PDF
            </button>
          </div>
        )}
      </div>

      {/* Event Form Modal */}
      {showEventForm && currentAcademicYear && (
        <EventFormNew
          key={selectedEvent?.id ?? 'new-event'}
          event={selectedEvent}
          categories={categories}
          academicYearId={currentAcademicYear.id}
          initialDate={selectedDate}
          onClose={() => {
            setShowEventForm(false);
            setSelectedEvent(null);
            setSelectedDate(undefined);
          }}
          onSaved={handleEventSaved}
          onDeleted={handleEventDeleted}
          readOnly={!canManage}
        />
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};
